
import Page from '@/Page.vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { shiftScrapRecordsStore, shiftsStore, shiftStationsStore } from '@/libs/shifts/+state/store';
import { CrudAction, CrudReponse, CrudResponseAction } from '@/libs/core/+state/models/crud-action';
import {
    AuditEscalationGroupModel,
    AuditModel,
    AuditShiftTypeModel,
    AuditWorkplaceModel,
    EscalationGroupTypeEnum,
    GroupModel,
    HourlyPlanTypeEnum,
    LocationModel,
    PermissionTypeEnum,
    PlanItemModel,
    PlanItemStateEnum,
    ProcessTypeEnum,
    ShiftModel,
    ShiftProductionModel,
    ShiftProductionRecordModel,
    ShiftScrapRecordModel,
    ShiftStateEnum,
    ShiftStationModel,
    StationModel,
    TenantUserModel,
} from '@/libs/Api';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import moment from 'moment/moment';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { auditsStore } from '@/libs/audits/+state/store';
import { auditWorkplacesStore } from '@/libs/audit-workplaces/+state/store';
import { auditShiftTypesStore } from '@/libs/audit-shift-types/+state/store';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import { Guid } from '@/libs/common/functions/guid';
import { cloneDeep } from 'lodash';
import { CrudQueryPredicate } from '@/libs/core/+state/models/crud-query-predicate';
import IssueSolvingItemSmall from '@/libs/issues-solving/components/IssueSolvingItemSmall.vue';
import IssueSolvingItem from '@/libs/issues-solving/components/IssueSolvingItem.vue';
import { auditEscalationGroupsStore } from '@/libs/audit-escalation-groups/+state/store';
import ShiftState from '@/libs/shifts/components/ShiftState.vue';
import { locationsStore } from '@/libs/locations/+state/store';
import { groupsStore } from '@/libs/groups/+state/store';
import { stationsStore } from '@/libs/stations/+state/store';
import { compare } from 'fast-json-patch';
import ShiftScrapRecordDialog from '@/libs/shifts/components/ShiftScrapRecordDialog.vue';
import { from } from 'linq-to-typescript';

@Component({
    components: { ShiftScrapRecordDialog, ShiftState, IssueSolvingItem, IssueSolvingItemSmall },
})
export default class ShiftWorkDetail extends Page {
    @Prop() id!: string;

    protected ShiftStateEnum: typeof ShiftStateEnum = ShiftStateEnum;
    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;
    protected tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;

    protected shiftProductionGroups: {
        fromTime: string | null;
        time: string;
        isActive: boolean;
        items: { shiftProduction: ShiftProductionModel; okCount: number | null; nokCount: number | null }[];
    }[] = [];

    protected stationIsOccupied = false;
    protected savingStationId: string | null = null;
    protected joiningStationId: string | null = null;
    protected joiningStationTimeout: any = null;

    private autoSaveTimeout: any = null;
    private autoSaveRemainingSecondsInterval: any = null;
    private autoSaveRemainingSeconds: number | null = null;
    private hasChanges = false;
    private isSavingFullShift = false;
    private updateInterval: any = null;
    private officerId: string | null = null;
    private shiftScrapRecordDialog = false;
    private activeScrapRecordId: string | null = null;
    private activeScrapRecordToDelete: ShiftScrapRecordModel | null = null;
    private shiftScrapRecordDeleteDialog = false;

    get isLoading() {
        return (
            shiftsStore.useGetter(CrudGetter.Pending) ||
            stationsStore.useGetter(CrudGetter.Pending, 'ShiftWorkDetail') ||
            shiftStationsStore.useGetter(CrudGetter.Pending, 'ShiftWorkDetail')
        );
    }

    get myStationIds(): string[] {
        return this.getMyStations().map((x) => x.id!);
    }

    get freeStations(): StationModel[] {
        const shiftStations: ShiftStationModel[] = shiftStationsStore.useGetter(CrudGetter.Data, 'ShiftWorkDetail') ?? [];
        const stations: StationModel[] = stationsStore.useGetter(CrudGetter.Data, 'ShiftWorkDetail') ?? [];
        const occupiedStations = shiftStations.filter((x) => x.workerId != null).map((x) => x.stationId!);
        return stations.filter((x) => !occupiedStations.contains(x.id!));
    }

    get isDisabled() {
        return this.getAvailableOfficers().length > 0 && !this.officerId;
    }

    get stations(): StationModel[] {
        const shift: ShiftModel | null = shiftsStore.useGetter(CrudGetter.Detail) ?? null;
        const shiftStations: ShiftStationModel[] = shiftStationsStore.useGetter(CrudGetter.Data, 'ShiftWorkDetail') ?? [];
        let items: StationModel[] = stationsStore.useGetter(CrudGetter.Data) ?? [];
        items = items.filter((x) => x.workplaceId == shift?.workplaceId);
        items = [
            ...items,
            ...shiftStations.filter((x) => !items.some((n) => n.id == x.stationId) && x.station).map((e) => e.station!),
        ];
        return items.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    }

    get isDeletingShiftScrapRecord(): boolean {
        return shiftScrapRecordsStore.useGetter(CrudGetter.Deleting) ?? false;
    }

    getMyStations(): StationModel[] {
        const shiftStations: ShiftStationModel[] = shiftStationsStore.useGetter(CrudGetter.Data, 'ShiftWorkDetail') ?? [];
        return shiftStations.filter((x) => x.workerId == this.tenantUser.id).map((x) => x.station!);
    }

    getStationWorker(stationId: string): string | null {
        const shiftStations: ShiftStationModel[] = shiftStationsStore.useGetter(CrudGetter.Data, 'ShiftWorkDetail') ?? [];
        return shiftStations.find((x) => x.stationId == stationId)?.workerId ?? null;
    }

    async setStationWorker(stationId: string, workerId: string | null) {
        shiftStationsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<ShiftStationModel>(
                undefined,
                [
                    { field: 'shiftId', op: 'eq', comparand: this.id },
                    { field: 'stationId', op: 'eq', comparand: stationId },
                ],
                undefined,
                false,
                'SetStationWorker'
            )
        );
        const shiftStations: ShiftStationModel[] = await this.subscribe(
            shiftStationsStore,
            CrudReponse.GetAll,
            'SetStationWorker'
        );
        if (workerId && shiftStations.length == 0) {
            shiftStationsStore.dispatch(CrudAction.Create, {
                item: {
                    shiftId: this.id,
                    stationId,
                    workerId,
                } as ShiftStationModel,
                key: 'SetStationWorker',
            });
            await this.subscribe(shiftStationsStore, CrudReponse.Create, 'SetStationWorker');
            await this.loadStationOccupation();
        } else if (workerId && shiftStations.length > 0) {
            const shiftStation = shiftStations[0];
            shiftStation.workerId = workerId;
            shiftStationsStore.dispatch(CrudAction.Update, {
                item: shiftStation,
                key: 'SetStationWorker',
            });
            await this.subscribe(shiftStationsStore, CrudReponse.Update, 'SetStationWorker');
            await this.loadStationOccupation();
        } else if (!workerId && shiftStations.length > 0) {
            const shiftStation = shiftStations[0];
            shiftStationsStore.dispatch(CrudAction.Delete, {
                id: shiftStation.id,
                key: 'SetStationWorker',
            });
            await this.subscribe(shiftStationsStore, CrudReponse.Delete, 'SetStationWorker');
            await this.loadStationOccupation();
        }
    }

    getAvailableOfficers(): TenantUserModel[] {
        const shift: ShiftModel | null = shiftsStore.useGetter(CrudGetter.Detail) ?? null;
        const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        const groups: GroupModel[] = groupsStore.useGetter(CrudGetter.Data) ?? [];
        if (!shift) {
            return [];
        }
        let locationId = shift.workplace!.locationId;
        let location = locations.find((x) => x.id == locationId);
        let trials = [locationId];
        while (location) {
            if (location.canPlanShifts) {
                break;
            } else {
                location = locations.find((x) => x.id == location?.parentalLocationId);
                if (trials.contains(location?.id)) {
                    break;
                }
            }
        }
        let items: TenantUserModel[] = [];
        if (location && location.canPlanShifts && location.locationGroups!.length > 0) {
            const groupIds = location.locationGroups!.map((x) => x.groupId);
            items = groups
                .filter((x) => groupIds.contains(x.id!))!
                .selectMany((x) => x.groupUsers!.select((u) => u.tenantUser!).toArray())
                .toArray()
                .reduce((acc, x) => {
                    if (!acc.find((a: any) => a.id == x.id)) {
                        acc.push(x);
                    }
                    return acc;
                }, [] as TenantUserModel[])
                .toArray();
        }
        if (shift.officer != null && !items.find((x) => x.id == shift.officerId)) {
            items.push(shift.officer);
        }
        return items;
    }

    get shift(): ShiftModel {
        return shiftsStore.useGetter(CrudGetter.Detail) ?? null;
    }

    get isSaving(): boolean {
        return shiftsStore.useGetter(CrudGetter.Saving);
    }

    get isReadonly(): boolean {
        const shift: ShiftModel | null = shiftsStore.useGetter(CrudGetter.Detail) ?? null;
        return shift?.shiftState == ShiftStateEnum.Closed;
    }

    get isLoadingChecklists(): boolean {
        return (
            planItemsStore.useGetter(CrudGetter.Pending, 'PerformedChecklists') ||
            auditWorkplacesStore.useGetter(CrudGetter.Pending, 'AvailableChecklists') ||
            auditShiftTypesStore.useGetter(CrudGetter.Pending, 'AvailableChecklists') ||
            auditsStore.useGetter(CrudGetter.Pending, 'AvailableChecklists')
        );
    }

    get isLoadingIssueSolving(): boolean {
        return (
            auditsStore.useGetter(CrudGetter.Pending, 'AvailableIssueSolving') ||
            auditWorkplacesStore.useGetter(CrudGetter.Pending, 'AvailableIssueSolving')
        );
    }

    get isLoadingShiftScrapRecords(): boolean {
        return shiftScrapRecordsStore.useGetter(CrudGetter.Pending);
    }

    get checkLists(): { audit: AuditModel; planItem: PlanItemModel | null }[] {
        const performedChecklists: PlanItemModel[] =
            planItemsStore.useGetter(CrudGetter.DataBasic, 'PerformedChecklists') ?? [];
        const shift: ShiftModel | null = shiftsStore.useGetter(CrudGetter.Detail) ?? null;
        const auditWorkplaces: AuditWorkplaceModel[] =
            auditWorkplacesStore.useGetter(CrudGetter.Data, 'AvailableChecklists') ?? [];
        const auditShiftTypes: AuditShiftTypeModel[] =
            auditShiftTypesStore.useGetter(CrudGetter.Data, 'AvailableChecklists') ?? [];
        const isLimitedWork = this.isLimitedWork();
        const myStations = this.getMyStations();
        const myStationsIds = myStations.map((x) => x.id!);
        const availableChecklists = (auditsStore.useGetter(CrudGetter.Data, 'AvailableChecklists') ?? []) as AuditModel[];
        return availableChecklists
            .filter((x) => {
                if (!x.useWorkplaceLimitation && !x.useShiftTypeLimitation) {
                    return true;
                } else if (x.useWorkplaceLimitation && x.useShiftTypeLimitation) {
                    if (
                        auditWorkplaces.some((n) => n.workplaceId == shift?.workplaceId && n.auditId == x.id) &&
                        auditShiftTypes.some((n) => n.shiftTypeId == shift?.shiftTypeId && n.auditId == x.id)
                    ) {
                        if (isLimitedWork) {
                            const auditWorkplace = auditWorkplaces.find(
                                (n) => n.workplaceId == shift?.workplaceId && n.auditId == x.id
                            );
                            if (auditWorkplace?.useStationLimitation) {
                                if (
                                    (auditWorkplace.auditWorkplaceStations ?? []).find((x) =>
                                        myStationsIds.contains(x.stationId!)
                                    )
                                ) {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        } else {
                            return true;
                        }
                    }
                } else if (x.useWorkplaceLimitation) {
                    if (auditWorkplaces.some((n) => n.workplaceId == shift?.workplaceId && n.auditId == x.id)) {
                        if (isLimitedWork) {
                            const auditWorkplace = auditWorkplaces.find(
                                (n) => n.workplaceId == shift?.workplaceId && n.auditId == x.id
                            );
                            if (auditWorkplace?.useStationLimitation) {
                                if (
                                    (auditWorkplace.auditWorkplaceStations ?? []).find((x) =>
                                        myStationsIds.contains(x.stationId!)
                                    )
                                ) {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        } else {
                            return true;
                        }
                    }
                } else if (x.useShiftTypeLimitation) {
                    if (auditShiftTypes.some((n) => n.shiftTypeId == shift?.shiftTypeId && n.auditId == x.id)) {
                        return true;
                    }
                }
                return false;
            })
            .map((x) => {
                return {
                    audit: x,
                    planItem: performedChecklists.filter((p) => p.auditId == x.id).shift() ?? null,
                };
            })
            .filter(x => x.audit.isActive || x.planItem);
    }

    get availableIssueSolving(): AuditModel[] {
        const shift: ShiftModel | null = shiftsStore.useGetter(CrudGetter.Detail) ?? null;
        let workplaces: string[] = [shift?.workplaceId ?? Guid.EmptyGuid()];
        let items: AuditModel[] = [...auditsStore.useGetter(CrudGetter.Data, 'AvailableIssueSolving')] ?? [];
        const auditWorkplaces: AuditWorkplaceModel[] =
            [...auditWorkplacesStore.useGetter(CrudGetter.Data, 'AvailableIssueSolving')] ?? [];
        if (workplaces && workplaces.length > 0) {
            items = items.filter((x) =>
                auditWorkplaces.filter((w) => w.auditId == x.id!).some((w) => workplaces.contains(w.workplaceId!))
            );
        }
        return items;
    }

    get isIssueSolvingsLoading(): boolean {
        return planItemsStore.useGetter(CrudGetter.Pending, 'PerformedIssueSolving');
    }

    get issueSolvings(): PlanItemModel[] {
        let items = (planItemsStore.useGetter(CrudGetter.Data, 'PerformedIssueSolving') ?? []) as PlanItemModel[];
        return items.filter(
            (x) => x.planItemState == PlanItemStateEnum.WaitingToPerform || (x.isWorkplaceShutdown && x.shutdownToDate == null)
        );
    }

    get autoSaveRemainingSecondsString(): string | null {
        if (this.autoSaveRemainingSeconds && this.autoSaveRemainingSeconds > 0) {
            const minutes = Math.floor(this.autoSaveRemainingSeconds / 60);
            const seconds = this.autoSaveRemainingSeconds - minutes * 60;
            return `${minutes.toFixed(0).padStart(2, '0')}:${seconds.toFixed(0).padStart(2, '0')}`;
        }
        return null;
    }

    get auditEscalationGroups(): AuditEscalationGroupModel[] {
        return auditEscalationGroupsStore.useGetter(CrudGetter.Data);
    }

    get shiftScrapRecords(): ShiftScrapRecordModel[] {
        return shiftScrapRecordsStore.useGetter(CrudGetter.Data) ?? [];
    }

    get canClose(): boolean {
        if (this.isLimitedWork()) {
            const shift: ShiftModel | null = shiftsStore.useGetter(CrudGetter.Detail) ?? null;
            if (
                shift &&
                shift.shiftState == ShiftStateEnum.Opened &&
                shift.fromDate &&
                shift.toDate &&
                new Date(shift.fromDate).getTime() <= new Date().getTime() &&
                new Date(shift.toDate).getTime() >= new Date().getTime()
            ) {
                return false;
            }
        }
        return true;
    }

    @Watch('id')
    async idChanged() {
        this.officerId = null;
        await this.loadShift();
        await this.loadStationOccupation();
        this.loadStations();
        this.loadIssueSolving();
        this.loadAuditEscalationGroups();
        this.loadPerformedChecklists();
        this.loadAvailableIssueSolving();
        this.loadShiftScrapRecords();
    }

    async mounted() {
        this.$store.subscribe((mutation) => {
            if (mutation.type === shiftsStore.getActionName(CrudResponseAction.GetSuccess)) {
                if (mutation.payload.item.id == this.id) {
                    this.updateShiftProductions();
                }
            } else if (mutation.type === shiftsStore.getActionName(CrudResponseAction.PartialUpdateSuccess)) {
                if (mutation.payload.id == this.id) {
                    this.updateShiftProductions();
                }
            } else if (mutation.type === shiftsStore.getActionName(CrudResponseAction.UpdateSuccess)) {
                if (mutation.payload.item.id == this.id) {
                    this.updateShiftProductions();
                }
            } else if (mutation.type === shiftsStore.getActionName(CrudResponseAction.DeleteSuccess)) {
                if (mutation.payload.data?.shift()?.id == this.id) {
                    this.$router.replace({ name: 'ShiftWorkList' });
                }
            }
        });
        this.updateInterval = setInterval(this.updateShiftProductionsActivity, 1000);
        await this.loadShift();
        await this.loadStationOccupation();
        this.loadStations();
        this.loadIssueSolving();
        this.loadAuditEscalationGroups();
        this.loadAvailableChecklists();
        this.loadPerformedChecklists();
        this.loadAvailableIssueSolving();
        this.loadShiftScrapRecords();
    }

    destroyed() {
        this.clearAutoSaveInterval();
        clearInterval(this.updateInterval);
    }

    async loadShift(silent = false) {
        shiftsStore.dispatch(CrudAction.Get, {
            id: this.id,
            silent,
        });
        const shift: ShiftModel | null = await this.subscribe(shiftsStore, CrudReponse.Get);
        this.officerId = shift?.officerId ?? null;
    }

    isLimitedWork(): boolean {
        return (
            this.hasPermissions([PermissionTypeEnum.ShiftWorkLimited]) && !this.hasPermissions([PermissionTypeEnum.ShiftWork])
        );
    }

    loadStations() {
        const shift: ShiftModel = shiftsStore.useGetter(CrudGetter.Detail)!;
        stationsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                {
                    field: 'name',
                    order: 'asc',
                    index: 1,
                },
                [{ field: 'workplaceId', op: 'eq', comparand: shift.workplaceId! }],
                undefined,
                false,
                'ShiftWorkDetail'
            )
        );
    }

    async loadStationOccupation() {
        const shift: ShiftModel = shiftsStore.useGetter(CrudGetter.Detail)!;
        shiftStationsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                undefined,
                [{ field: 'shiftId', op: 'eq', comparand: shift.id! }],
                undefined,
                false,
                'ShiftWorkDetail'
            )
        );
        await this.subscribe(shiftStationsStore, CrudReponse.GetAll, 'ShiftWorkDetail');
    }

    loadAvailableChecklists() {
        const shift: ShiftModel = shiftsStore.useGetter(CrudGetter.Detail)!;
        auditsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                { field: 'name', index: 1, order: 'asc' },
                [
                    {
                        field: 'processType',
                        op: 'eq',
                        comparand: ProcessTypeEnum.Checklist,
                    },
                ],
                undefined,
                false,
                'AvailableChecklists'
            )
        );
        auditWorkplacesStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                { field: 'id', index: 1, order: 'asc' },
                [
                    { field: 'audit.processType', op: 'eq', comparand: ProcessTypeEnum.Checklist },
                    { field: 'workplaceId', op: 'eq', comparand: shift.workplaceId },
                ],
                undefined,
                false,
                'AvailableChecklists'
            )
        );
        auditShiftTypesStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                { field: 'id', index: 1, order: 'asc' },
                [{ field: 'audit.processType', op: 'eq', comparand: ProcessTypeEnum.Checklist }],
                undefined,
                false,
                'AvailableChecklists'
            )
        );
    }

    loadPerformedChecklists() {
        planItemsStore.dispatch(
            CrudAction.GetAllBasic,
            createCrudQueryPayload(
                { field: 'createdAt', order: 'asc', index: 1 },
                [
                    { field: 'processType', op: 'eq', comparand: ProcessTypeEnum.Checklist },
                    { field: 'shiftId', op: 'eq', comparand: this.id },
                ],
                undefined,
                false,
                'PerformedChecklists'
            )
        );
    }

    loadAvailableIssueSolving() {
        let filters: CrudQueryPredicate[] = [
            {
                field: 'processType',
                op: 'eq',
                comparand: ProcessTypeEnum.IssueSolving,
            },
        ];
        if (!this.tenantUser.accessAllCategories) {
            filters.push({ field: 'category.categoryUsers.tenantUserId', op: 'eq', comparand: this.tenantUser.id! });
        }
        filters.push({ field: 'isActive', op: 'eq', comparand: true });
        if (!this.tenantUser.accessAllCategories) {
            filters.push({ field: 'category.categoryUsers.tenantUserId', op: 'eq', comparand: this.tenantUser.id! });
        }
        const payload = createCrudQueryPayload<AuditModel>(
            { field: 'name', index: 1, order: 'asc' },
            filters,
            undefined,
            false,
            'AvailableIssueSolving'
        );
        auditsStore.dispatch(CrudAction.GetAll, payload);
        const payloadWorkplaces = cloneDeep(payload);
        payloadWorkplaces.query.data.orderby.forEach((e) => {
            e.field = `audit.${e.field}`;
        });
        payloadWorkplaces.query.data.predicates.forEach((e) => {
            e.field = `audit.${e.field}`;
        });
        auditWorkplacesStore.dispatch(CrudAction.GetAll, payloadWorkplaces);
    }

    loadIssueSolving() {
        const shift: ShiftModel = shiftsStore.useGetter(CrudGetter.Detail)!;
        planItemsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<PlanItemModel>(
                { field: 'time', index: 1, order: 'desc' },
                [
                    {
                        field: 'processType',
                        op: 'eq',
                        comparand: ProcessTypeEnum.IssueSolving,
                    },
                    {
                        field: 'id',
                        op: 'wrap',
                        comparand: [
                            {
                                field: 'planItemState',
                                op: 'eq',
                                comparand: 'WaitingToPerform',
                                junction: 'or',
                            },
                            {
                                field: 'id',
                                op: 'wrap',
                                comparand: [
                                    {
                                        field: 'isWorkplaceShutdown',
                                        op: 'eq',
                                        comparand: true,
                                    },
                                    {
                                        field: 'shutdownToDate',
                                        op: 'eq',
                                        comparand: null,
                                    },
                                ],
                                junction: 'or',
                            },
                        ],
                    },
                    {
                        field: 'workplaceId',
                        op: 'eq',
                        comparand: shift.workplaceId,
                    },
                ],
                undefined,
                false,
                'PerformedIssueSolving'
            )
        );
    }

    protected loadAuditEscalationGroups() {
        auditEscalationGroupsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<AuditEscalationGroupModel>(undefined, [
                { field: 'escalationGroupType', op: 'eq', comparand: EscalationGroupTypeEnum.IssueEscalation },
            ])
        );
    }

    protected loadShiftScrapRecords() {
        const predicates: CrudQueryPredicate[] = [{ field: 'shiftId', op: 'eq', comparand: this.id }];
        if (this.isLimitedWork()) {
            predicates.push({
                field: 'stationId',
                op: 'in',
                comparand: this.getMyStations().map((x) => x.id!),
            });
        }
        shiftScrapRecordsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                {
                    field: 'recordTime',
                    index: 1,
                    order: 'desc',
                },
                predicates,
                undefined,
                false
            )
        );
    }

    updateShiftProductions() {
        if (this.isSavingFullShift) {
            return;
        }
        const shift = { ...shiftsStore.useGetter(CrudGetter.Detail) } as ShiftModel;
        const shiftProductionGroups: {
            fromTime: string | null;
            time: string;
            isActive: boolean;
            items: { shiftProduction: ShiftProductionModel; okCount: number | null; nokCount: number | null }[];
        }[] = [];
        const fromDate = moment(shift.fromDate!).add(60, 'minutes').toDate();
        let toDate = new Date(shift.toDate!);
        if (toDate.getMinutes() > 0) {
            toDate = moment(toDate).startOf('hour').add(1, 'hour').toDate();
        }
        let time = fromDate;
        while (time <= toDate) {
            const groupTimeMoment = moment(time).isAfter(shift.toDate) ? moment(shift.toDate) : moment(time);
            const isLastHourGroup = groupTimeMoment.isSame(moment(shift.toDate));
            const thresholdMinutesForGroup = isLastHourGroup ? 30 : 10;
            shiftProductionGroups.push({
                fromTime: moment(time).subtract(60, 'minutes').format('YYYY-MM-DDTHH:mm:ss'),
                time: groupTimeMoment.format('YYYY-MM-DDTHH:mm:ss'),
                isActive: groupTimeMoment.isBefore(moment().add(thresholdMinutesForGroup, 'minutes')),
                items: [],
            });
            time = new Date(time.getTime() + 60 * 60 * 1000);
        }
        for (const shiftProduction of shift.shiftProductions ?? []) {
            for (const shiftProductionRecord of shiftProduction.shiftProductionRecords ?? []) {
                if (!shiftProductionGroups.some((x) => this.areTimesEqual(x.time, shiftProductionRecord.recordTime!))) {
                    const recordTimeMoment = moment(shiftProductionRecord.recordTime!);
                    const isRecordLastHour = recordTimeMoment.isSame(moment(shift.toDate));
                    const recordThresholdMinutes = isRecordLastHour ? 30 : 10;
                    shiftProductionGroups.push({
                        fromTime: null,
                        time: shiftProductionRecord.recordTime!,
                        isActive: recordTimeMoment.isBefore(moment().add(recordThresholdMinutes, 'minutes')),
                        items: [],
                    });
                }
            }
        }
        for (const shiftProductionGroup of shiftProductionGroups) {
            time = new Date(shiftProductionGroup.time);
            shiftProductionGroup.items = (shift.shiftProductions ?? []).map((p) => {
                const shiftProductionRecord = (p.shiftProductionRecords ?? [])
                    .filter((r) => this.areTimesEqual(r.recordTime!, shiftProductionGroup.time))
                    .shift();
                return {
                    shiftProduction: p,
                    okCount: shiftProductionRecord?.okCount ?? null,
                    nokCount: shiftProductionRecord?.nokCount ?? null,
                };
            });
        }
        this.shiftProductionGroups = shiftProductionGroups;
    }

    updateShiftProductionsActivity() {
        if (this.shiftProductionGroups) {
            const shift = { ...shiftsStore.useGetter(CrudGetter.Detail) } as ShiftModel;
            const shiftEndMoment = moment(shift.toDate);
            let shouldUpdate = false;
            this.shiftProductionGroups.forEach((x) => {
                const isLastHour = moment(x.time).isSame(shiftEndMoment);
                const thresholdMinutes = isLastHour ? 30 : 10;
                const newActive = moment(x.time).isBefore(moment().add(thresholdMinutes, 'minutes'));
                if (x.isActive !== newActive) {
                    shouldUpdate = true;
                }
            });
            if (shouldUpdate) {
                this.shiftProductionGroups.forEach((x) => {
                    const isLastHour = moment(x.time).isSame(shiftEndMoment);
                    const thresholdMinutes = isLastHour ? 30 : 10;
                    x.isActive = moment(x.time).isBefore(moment().add(thresholdMinutes, 'minutes'));
                });
            }
        }
    }

    async onOpenShiftClick() {
        await this.loadShift();
        const tenantUser = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
        const item = { ...shiftsStore.useGetter(CrudGetter.Detail) } as ShiftModel;
        item.officerId = this.officerId;
        item.shiftState = ShiftStateEnum.Opened;
        item.openedById = tenantUser.id;
        item.openedBy = tenantUser;
        item.openedAt = moment().format('YYYY-MM-DDTHH:mm:ss');
        shiftsStore.dispatch(CrudAction.Update, { item });
    }

    async onActivateAutoSave() {
        this.hasChanges = true;
        clearTimeout(this.autoSaveTimeout);
        this.clearAutoSaveInterval();
        this.autoSaveTimeout = setTimeout(() => {
            this.autoSaveRemainingSeconds = 10;
            this.autoSaveRemainingSecondsInterval = setInterval(() => {
                this.autoSaveRemainingSeconds!--;
                if (this.autoSaveRemainingSeconds == 0) {
                    this.onSave();
                }
            }, 1000);
        }, 5000);
    }

    clearAutoSaveInterval() {
        clearTimeout(this.autoSaveTimeout);
        clearInterval(this.autoSaveRemainingSecondsInterval);
        this.autoSaveRemainingSecondsInterval = null;
        this.autoSaveRemainingSeconds = null;
    }

    /**
     * Pomocná funkce pro porovnání časů - ignoruje timezone rozdíly
     */
    private areTimesEqual(time1: string | Date, time2: string | Date): boolean {
        return moment(time1).isSame(moment(time2));
    }

    /**
     * Pomocná funkce pro normalizaci času na formát bez timezone
     */
    private normalizeRecordTime(time: string | Date): string {
        return moment(time).format('YYYY-MM-DDTHH:mm:ss');
    }

    async onSave() {
        // Ochrana proti vícenásobnému volání
        if (this.isSavingFullShift) {
            return;
        }

        this.clearAutoSaveInterval();
        this.isSavingFullShift = true;

        try {
            await this.loadShift(true);
            const item = { ...shiftsStore.useGetter(CrudGetter.Detail) } as ShiftModel;
            (item.shiftProductions ?? []).forEach((p) => {
                const savedRecords = p.shiftProductionRecords ?? [];
                for (const record of savedRecords) {
                    const shiftProductionRecord = this.shiftProductionGroups
                        .filter((x) => this.areTimesEqual(x.time, record.recordTime!))
                        .shift()
                        ?.items.filter((x) => x.shiftProduction.id == p.id)
                        .shift();
                    if (shiftProductionRecord != null && (shiftProductionRecord.okCount || shiftProductionRecord.nokCount)) {
                        record.okCount =
                            shiftProductionRecord.okCount && Number(shiftProductionRecord.okCount) >= 0
                                ? Number(shiftProductionRecord.okCount)
                                : null;
                        record.nokCount =
                            shiftProductionRecord.nokCount && Number(shiftProductionRecord.okCount) >= 0
                                ? Number(shiftProductionRecord.nokCount)
                                : null;
                    } else {
                        savedRecords.splice(savedRecords.indexOf(record), 1);
                    }
                }
                this.shiftProductionGroups.forEach((x) => {
                    const shiftProductionRecord = x.items.filter((x) => x.shiftProduction.id == p.id).shift();
                    if (shiftProductionRecord) {
                        const okCount =
                            shiftProductionRecord.okCount && Number(shiftProductionRecord.okCount) >= 0
                                ? Number(shiftProductionRecord.okCount)
                                : null;
                        const nokCount =
                            shiftProductionRecord.nokCount && Number(shiftProductionRecord.okCount) >= 0
                                ? Number(shiftProductionRecord.nokCount)
                                : null;
                        if (
                            savedRecords.filter((r) => this.areTimesEqual(r.recordTime!, x.time)).length == 0 &&
                            (okCount != null || nokCount != null)
                        ) {
                            savedRecords.push({
                                recordTime: this.normalizeRecordTime(x.time),
                                okCount,
                                nokCount,
                            });
                        }
                    }
                });
                p.okCount = savedRecords.filter((r) => r.okCount != null).reduce((a, b) => a + b.okCount!, 0);
                p.nokCount = savedRecords.filter((r) => r.nokCount != null).reduce((a, b) => a + b.nokCount!, 0);
            });
            item.okCount = (item.shiftProductions ?? []).reduce((a, b) => a + b.okCount!, 0);
            item.nokCount = (item.shiftProductions ?? []).reduce((a, b) => a + b.nokCount!, 0);
            shiftsStore.dispatch(CrudAction.Update, { item, silent: true });
            await this.subscribe(shiftsStore, CrudReponse.Update);
            this.hasChanges = false;
        } catch (error) {
            console.error('Error saving shift:', error);
            // Zde můžeš přidat notifikaci pro uživatele pokud potřebuješ
        } finally {
            this.isSavingFullShift = false;
        }
    }

    public async onJoinStationClick(stationId: string) {
        this.stationIsOccupied = false;
        this.joiningStationId = stationId;
        this.savingStationId = null;
        this.joiningStationTimeout = setTimeout(async () => {
            shiftStationsStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload(
                    undefined,
                    [
                        { field: 'shiftId', op: 'eq', comparand: this.id! },
                        { field: 'stationId', op: 'eq', comparand: stationId },
                    ],
                    undefined,
                    false,
                    'JoinShiftStation'
                )
            );
            const shiftStations = (await this.subscribe(
                shiftStationsStore,
                CrudReponse.GetAll,
                'JoinShiftStation'
            )) as ShiftStationModel[];
            if (shiftStations.length > 0) {
                this.stationIsOccupied = true;
                return;
            }
            shiftStationsStore.dispatch(CrudAction.Create, {
                item: { shiftId: this.id, stationId, workerId: this.tenantUser.id } as ShiftStationModel,
                key: 'JoinShiftStation',
            });
            await this.subscribe(shiftStationsStore, CrudReponse.Create, 'JoinShiftStation');
            await this.loadStationOccupation();
            this.joiningStationId = null;
            this.savingStationId = null;
        }, 5000);
    }

    onCancelJoinStationClick() {
        clearTimeout(this.joiningStationTimeout);
        this.stationIsOccupied = false;
        this.joiningStationId = null;
        this.savingStationId = null;
    }

    onShiftScrapRecordClick() {
        this.activeScrapRecordId = null;
        this.shiftScrapRecordDialog = true;
    }

    getShiftScrapRecords(
        fromTime: string | null,
        toTime: string | null,
        productId: string,
        isLast: boolean
    ): ShiftScrapRecordModel[] {
        if (!fromTime || !toTime) {
            return [];
        }
        const fromTimeValue = new Date(fromTime);
        const toTimeValue = new Date(toTime);
        const shiftScrapRecords: ShiftScrapRecordModel[] = shiftScrapRecordsStore.useGetter(CrudGetter.Data) ?? [];
        return shiftScrapRecords.filter(
            (x) =>
                x.productId == productId &&
                new Date(x.recordTime!).getTime() >= fromTimeValue.getTime() &&
                (new Date(x.recordTime!).getTime() < toTimeValue.getTime() ||
                    (isLast && new Date(x.recordTime!).getTime() <= toTimeValue.getTime()))
        );
    }

    getShiftScrapRecordCount(fromTime: string | null, toTime: string | null, productId: string, isLast: boolean): number {
        return this.getShiftScrapRecords(fromTime, toTime, productId, isLast).reduce((acc, c) => acc + (c.scrapCount ?? 0), 0);
    }

    onEditScrapRecordClick(scrapRecord: ShiftScrapRecordModel) {
        this.activeScrapRecordId = scrapRecord!.id!;
        this.shiftScrapRecordDialog = true;
    }

    onDeleteScrapRecordClick(scrapRecord: ShiftScrapRecordModel) {
        this.activeScrapRecordToDelete = scrapRecord;
        this.shiftScrapRecordDeleteDialog = true;
    }

    async deleteScrapRecord() {
        shiftScrapRecordsStore.dispatch(CrudAction.Delete, {
            id: this.activeScrapRecordToDelete!.id,
        });
        await this.subscribe(shiftScrapRecordsStore, CrudReponse.Delete);
        this.shiftScrapRecordDeleteDialog = false;
        this.activeScrapRecordToDelete = null;
    }

    getShiftHourlyPlan(shift: ShiftModel | null): number {
        if (!shift) {
            return 0;
        }
        if (shift.hourlyPlanType === HourlyPlanTypeEnum.ByCycleTime) {
            return shift.hourlyPlanCycleTime ?? 0;
        }
        return shift.hourlyPlan ?? 0;
    }

    getHourlyPlan(from: string, to: string) {
        const shift: ShiftModel | null = shiftsStore.useGetter(CrudGetter.Detail) ?? null;
        const pFromTime = new Date(from).getTime();
        const pToTime = new Date(to).getTime();
        if (pFromTime < pToTime) {
            return Math.round(this.getShiftHourlyPlan(shift) * ((pToTime - pFromTime) / 3600000));
        }
        return 0;
    }
}
